import { getOrganizationDisplayName } from '@app/src/components/Table/Cells/CompanyCell'
import CompanyAvatar from '@app/src/pages/ResourceCollection/Collections/CellComponents/CompanyAvatar'
import type { Provider } from '@app/src/types/organizations'
import { resolveCompanyLogoImageUrl } from '@app/src/utils/resolveCompanyLogoImageUrl'
import { Divider, Stack, Typography } from '@mui/material'
import React from 'react'
import ReactCountryFlag from 'react-country-flag'
import useLocalisedCountryName from '../hooks/useLocalisedCountryName'
import ProviderContactCard from './ProviderContactCard'
import CompanyOverview from './ResourceDetails/CompanyOverview'
import { AvatarSize } from './Ui/Avatar'

interface ProviderDrawerContentProps {
  provider: Provider
}

const ProviderDrawerContent: React.FC<ProviderDrawerContentProps> = ({ provider }) => {
  const providerName = getOrganizationDisplayName(provider)
  const countryCode = provider.country?.countryCode
  const { getLocalisedCountryName } = useLocalisedCountryName()
  const localisedCountryName = getLocalisedCountryName(countryCode)
  const industry = provider.organization?.industryNaceCode?.description

  return (
    <Stack pt={2} flexGrow={1}>
      <Stack alignItems="center">
        <CompanyAvatar
          organizationName={providerName}
          imageUrl={resolveCompanyLogoImageUrl(provider)}
          size={AvatarSize.XXL}
        />
        <Typography variant="h5" mt={2} color="textPrimary">
          {providerName}
        </Typography>
        <Stack
          direction="row"
          gap={0.5}
          flexWrap="wrap"
          alignItems="center"
          maxWidth="90%"
          justifyContent="center"
          mt={1}
        >
          {countryCode && (
            <Stack direction="row" gap={1} alignItems="center">
              <ReactCountryFlag
                countryCode={countryCode}
                svg
                style={{ fontSize: '1.5em' }}
                title={localisedCountryName}
              />
              <Typography variant="body2" color="text.secondary">
                {localisedCountryName}
                {industry && ','}
              </Typography>
            </Stack>
          )}
          {industry && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {industry}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack flexGrow={1} mt={2}>
        <CompanyOverview provider={provider} />
      </Stack>

      <Stack>
        <Divider sx={{ m: 2 }} />
        <ProviderContactCard provider={provider} />
      </Stack>
    </Stack>
  )
}

export default ProviderDrawerContent
